<template>
<div>
    <div class="form-row">
        <div class="col">
            <form-input label="Name on the Account" v-model="formData.name_on_account" :errors="errors.name_on_account" type="text" :maxlength="128" />
        </div>
    </div>

    <div class="form-row">
        <div class="col">
            <form-input label="Friendly Name for the Account" v-model="formData.name" :errors="errors.name" type="text" :maxlength="48" />
        </div>
    </div>

    <div class="form-row">
        <div class="col">
            <form-input
                label="ACH Routing Number"
                v-model="formData.routing_number"
                :errors="errors.routing_number"
                type="integer-string"
                maxlength="9"
                extraclasses=""
                @update:model-value="onUpdateRoutingNumber()"
                :helperHtml="bankNameHtml"
                :popoverHelperHtml="'<p>Your bank&rsquo;s Routing Number is the first set of numbers on the bottom of your check. It is 9 digits long.</p>'"
            />

        </div>

        <div class="col">
            <form-input
                label="Account Number"
                v-model="formData.account_number"
                :errors="errors.account_number"
                type="integer-string"
                maxlength="17"
                :popoverHelperHtml="'<p>Your Account Number is the second set of numbers on the bottom of your check. It can vary in length.</p>'"
            />

            <p v-if="formData.account_number_last_4"><small>Leave blank if you do not wish to update it.</small></p>
        </div>
    </div>

    <div class="form-row">
        <div class="col">
            <form-input label="Starting Check #" v-model="formData.check_number" :errors="errors.check_number" type="integer" :maxlength="6" />
        </div>
    </div>

    <div class="form-row">
        <div class="col">
            <form-input label="Street" v-model="formData.address_1" :errors="errors.address_1" type="text" :maxlength="200" />
        </div>
    </div>

    <div class="form-row">
        <div class="col">
            <form-input label="Street 2" v-model="formData.address_2" :errors="errors.address_2" type="text" :maxlength="200" />
        </div>
    </div>

    <div class="form-row">
        <div class="col">
            <form-input label="City" v-model="formData.city" :errors="errors.city" type="text" :maxlength="200" />
        </div>

        <div class="col">
            <form-input label="State" v-model="formData.state" :errors="errors.state" type="select" :options="stateOptions" />
        </div>

        <div class="col">
            <form-input label="Postcode" v-model="formData.postal_code" :errors="errors.postal_code" type="text" :maxlength="10" />
        </div>
    </div>

    <h5 class="mt-4">Default purpose(s)</h5>
    <p>
        <form-input label="Billing" v-model="formData.is_default_billing" :errors="errors.is_default_billing" type="checkbox" /> <br />
        <form-input label="Net Pay" v-model="formData.is_default_net_pay" :errors="errors.is_default_net_pay" type="checkbox" /> <br />
        <form-input label="Taxes" v-model="formData.is_default_taxes" :errors="errors.is_default_taxes" type="checkbox" /> <br />
    </p>

    <form-errors :errors="formErrors" />
</div>
</template>

<script>
import FormMixin from '@/mixins/Form'
import states from '@/states'

export default {
    props: ['initialValue', ],
    mixins: [FormMixin, ],
    computed: {
        bankNameHtml() {
            if (!this.formData.bank_name) {
                return ''
            }

            return '<i class="fas fa-fw fa-check mr-hf text-primary" aria-hidden="true"></i> ' + this.formData.bank_name
        },
        stateOptions() {
            const result = []
            states.forEach((s) => {
                result.push({text: s.name.toUpperCase(), value: s.abbreviation})
            })
            return result
        },
    },
    data() {
        return {
            formData: this.makeFormData(),
            formRules: {
                name: {presence: {allowEmpty: false}, },
                name_on_account: {presence: {allowEmpty: false}, },
                routing_number: {presence: {allowEmpty: false}, },
                check_number: {presence: {allowEmpty: false}, },
                address_1: {presence: {allowEmpty: false}, },
                city: {presence: {allowEmpty: false}, },
                state: {presence: {allowEmpty: false}, },
                postal_code: {presence: {allowEmpty: false}, },
            },
            formLabels: {
                name: 'Friendly name',
            },
        }
    },
    methods: {
        makeFormData() {
            return {
                name: this.initialValue.name ? this.initialValue.name : '',
                name_on_account: this.initialValue.name_on_account ? this.initialValue.name_on_account : '',
                routing_number: this.initialValue.routing_number ? this.initialValue.routing_number : '',
                account_number: this.initialValue.account_number ? this.initialValue.account_number : '',
                check_number: this.initialValue.check_number ? this.initialValue.check_number : '',
                address_1: this.initialValue.address_1 ? this.initialValue.address_1 : '',
                city: this.initialValue.city ? this.initialValue.city : '',
                state: this.initialValue.state ? this.initialValue.state : '',
                postal_code: this.initialValue.postal_code ? this.initialValue.postal_code : '',
                bank_name: this.initialValue.bank_name ? this.initialValue.bank_name : '',
                is_default_billing: this.initialValue.is_default_billing ? true : false,
                is_default_net_pay: this.initialValue.is_default_net_pay ? true : false,
                is_default_taxes: this.initialValue.is_default_taxes ? true : false,
            }
        },
        setErrors(errors) {
            Object.assign(this.errors, errors)
        },
        onUpdateRoutingNumber() {
            const routingNumber = this.formData.routing_number
            if (!routingNumber) {
                this.formData.bank_name = ''
                return
            }

            // only ask the API to validate if it looks like a valid-looking number
            if (routingNumber.length != 9) {
                this.formData.bank_name = ''
                return
            }

            this.errors.routing_number = []
            this.$api.post('/validate/brn', {'routing_number': routingNumber}).then((resp) => {
                this.formData.bank_name = resp['bank_name']
            }).catch((errors) => {
                this.errors.routing_number = errors['routing_number']
                this.formData.bank_name = ''
            })
        },
        getFormRules() {
            const rules = JSON.parse(JSON.stringify(this.formRules))
            if (!this.initialValue.account_number_last_4) {
                rules.account_number = {presence: {allowEmpty: false}, }
            }
            return rules
        },
        validate() {
            const originalValidate = FormMixin.methods.validate.bind(this)
            let isValid = originalValidate()

            if (!this.formData.bank_name) {
                isValid = false
                this.errors.routing_number.push('Routing number not found.')
            }

            return isValid
        },
    },
}

</script>
